import React, { useState, useEffect } from "react";
import { useDispatch } from 'react-redux';
import PropTypes from "prop-types";
import { Formik } from 'formik';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import * as yup from 'yup';
import { FluentDatePicker } from '../../../../components/FluentUI/Fluent-DatePicker';
import LoadingOverlay from '../../../../components/loading-ovelay/loading-overlay';
import { errorNotification, successNotification } from "../../../../redux/notifications";
import {
    ChangeTaskAction,
    CancelTaskRegular
} from '../../../../services/api/task-api';
import { ToBase64 } from '../../../../services/api/file-api';
import { Alert } from 'react-bootstrap';
import dayjs from '../../../../util/dayjs-config';
import { businessAdd, businessDiff } from '../../../../util/business-days';
import DropdownMain from '../../../../components/dropdown-main/DropdownMain';
import '../tasks-reassign-panel/tasks-reassign-panel.scss';
import './task-custom-status-panel.scss';
// Side-effect import: this panel renders the same `.invalid-file` / `.invalid-attachment-icon`
// markup as `DisplayAlert.jsx`. The rules originally lived in the deleted `tasks-comments.scss`;
// they were migrated to `DisplayAlert.scss` in TAD-5777.
import '../tasks-comments/DisplayAlert/DisplayAlert.scss';

const TaskCustomStatusPanel = ({
    show,
    onHide,
    type,
    task,
    refreshTasks,
    customStatusID,
    customStatusAction,
    customActionID,
    customActionName
}) => {
    const [comment, setComment] = useState("");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [noteAction, setNoteAction] = useState(customStatusID);
    const [noteActionName, setNoteActionName] = useState(customStatusAction);
    const [showAlert, setShowAlert] = useState(false);
    const [alertVariant, setAlertVariant] = useState("danger");
    const [alertMessage, setAlertMessage] = useState("");
    const [alertAction, setAlertAction] = useState("");
    const [hasDueDate, setHasDueDate] = useState(false);
    const [newDueDate, setNewDueDate] = useState("");
    const [hasExtendReminder, setHasExtendReminder] = useState(false);
    const [currentSelectedCustomStatus, setCurrentSelectedCustomStatus] = useState([]);
    const [selectedEGUnit, setSelectedEGUnit] = useState("Business Days");
    const [selectedEGValue, setSelectedEGValue] = useState(1);
    const [isEGValid, setIsEGValid] = useState(true);

    const dispatch = useDispatch();

    useEffect(() => {
        SetStatusType();
    }, [noteAction])

    const extensionDuration = [
        { id: "Days", name: "Days" },
        { id: "Business Days", name: "Business Days" },
        // { id: "Weeks", name: "Weeks" },
        // { id: "Months", name: "Months" },
        // { id: "Years", name: "Years" },
    ];

    const SetStatusType = () => {
        const newFormType = task?.Workflow?.StatusListItems?.filter((status) => status.Id === noteAction)[0] ?? {};
        setCurrentSelectedCustomStatus(newFormType);
        if (newFormType.DueDateExtension) {
            setHasDueDate(true);
            let newDate = new Date();
            if (newFormType?.DueDateExtensionType == "Business Days") {
                newDate = businessAdd(new Date(), newFormType?.DueDateExtensionDefault);
                setNewDueDate(newDate)
            } else {
                newDate.setDate(newDate.getDate() + newFormType?.DueDateExtensionDefault);
                setNewDueDate(newDate)
            }
        } else {
            setHasDueDate(false);
        }

        if (newFormType.ExtendReminderName != null) {
            setHasExtendReminder(true);
        } else {
            setHasExtendReminder(false);
        }
    }

    const handleStatusChange = async (customObj) => {
        const desc = currentSelectedCustomStatus?.Description ?? "";
        const isCancel = desc.includes("Cancel");
        const actionFn = (() => {
            if (isCancel) return CancelTaskRegular;
            return ChangeTaskAction;
        })();
        const successHeader = isCancel
          ? "Task Status Cancelled"
          : "Task Status Updated";

        try {
            await actionFn(customObj);
            dispatch(successNotification({
                header: successHeader,
                message: `The task "${task?.Name ?? ""}" has been updated successfully`
            }));
        } catch (err) {
            console.error(err);
            dispatch(errorNotification({
                header: "Task Status Update Failed",
                message: err?.message ?? String(err)
            }));
        } finally {
            onHide();
            refreshTasks();
        }
    }

    const CustomStatusSubmit = (values) => {
        let customObj = {
            TaskId: values.Id,
            WorkflowActionId: customActionID,
            WorkflowStatusId: noteAction,
            Action: noteActionName,
            Note: comment,
            ActionId: currentSelectedCustomStatus.Active ? 10 : 2, //Updated/Completed Enum
            Files: selectedFiles,
            DueDate : newDueDate,
            TaskExtendedBy : currentSelectedCustomStatus?.ExtendReminderName !== null ? selectedEGValue : null,
            TaskExtendedByType : currentSelectedCustomStatus?.ExtendReminderName !== null ? selectedEGUnit : null,
        }

        Promise.all(
            selectedFiles.map((file) => {
                return (
                    (ToBase64(file).then(
                        (result) => {
                            let mimetype = result.substr(result.indexOf(':') + 1, result.indexOf(';') - result.indexOf(':') - 1);
                            let thedocAsBytes = result.split(',')[1];
                            return { FileName: file.name, FileContentType: mimetype, FileByteArray: thedocAsBytes };
                        }
                    )
                    )
                )
            })
        ).then(
            async (convertedFiles) => {
                customObj.Files = convertedFiles;
                await handleStatusChange(customObj);
            }
        )
    }

    //#region File attachement fucntions
    const deleteBrowseFile = (e, name) => {
        let files = selectedFiles;
        let idx = files.findIndex(file => file.name === name);
        files.splice(idx, 1);
        setSelectedFiles([...files]);
    }

    const drop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        let files = selectedFiles;
        let newSelectedFiles = Array.from(e.dataTransfer.files);
        newSelectedFiles.forEach(file => {
            files.push(file);
        })
        setSelectedFiles([...files]);

        e.dataTransfer.clearData();
    }

    const dragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
    }

    const dragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    const dragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    const shortenFileName = (fileName) => {
        if (fileName.length > 20) {
            let fileNameArr = fileName.split('.')
            let fileExtension = fileNameArr[fileNameArr.length - 1];

            return (fileName.slice(0, 20 - fileExtension.length - 4) + "...." + fileExtension);
        } else {
            return fileName;
        }
    }

    const browseFile = (e) => {
        let files = selectedFiles;
        let newSelectedFiles = Array.from(e.target.files);
        e.target.value = null;
        if (files.length === 0) {
            newSelectedFiles.forEach(file => {
                if (!checkInvalidFile(file)) {
                    return;
                }
                files.push(file);
            });
        } else {
            let fileFound = false;
            newSelectedFiles.forEach(file => {
                for (let i = 0; i < files.length; i++) {
                    if (files[i].name === file.name) {
                        fileFound = true;
                    }
                }
                if (!fileFound) {
                    if (!checkInvalidFile(file)) {
                        return;
                    }
                    files.push(file);
                }
            });
        }
        setSelectedFiles([...files]);
    }

    const checkInvalidFile = (fileObj) => {
        const validFileTypes = ['.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.pdf', '.xlsm'];

        if (fileObj) {
            if (!validFileTypes.map((type) => { return fileObj.name.includes(type) }).includes(true)) {
                setShowAlert(true);
                setAlertVariant("warning");
                setAlertAction("Cannot Attach:");
                setAlertMessage("File type is not supported");
                return false;
            }
            else if (fileObj.size > 52428800) {
                setShowAlert(true);
                setAlertVariant("warning");
                setAlertAction("Cannot Attach:");
                setAlertMessage("File size exceeds 50MB");
                return false;
            }
            return true;
        } else {
            setShowAlert(true);
            setAlertVariant("warning");
            setAlertAction("Cannot Attach:");
            setAlertMessage("File cannot be uploaded");
            return false
        }
    }

    const displayAlert = () => {
        setTimeout(() => {
            setShowAlert(false);
        }, 5000)

        return <>{
            alertVariant == "warning" ?

                <div className="invalid-file">
                    <div className="invalid-attachment-icon"></div>
                    {alertMessage !== "File type is not supported" ?
                        <p className="file-size-invalid">File size must be under 50MB</p>
                        :
                        <p>File format must be .pdf, .doc, .docx, .ppt, .pptx, .xls, .xlsx, .xlsm</p>
                    }
                </div>
                :
                <Alert show={showAlert} onClose={() => setShowAlert(false)} variant={alertVariant} dismissible>
                    <Alert.Heading>{alertAction}</Alert.Heading>
                    <p>{alertMessage}</p>
                    <hr />
                </Alert>
        }</>
    }

    //#endregion

    const validateEG = (value, unit) => {
        let days = value;
        let bdays = value;
        if (unit == "Years") {
            days = 365 * value;
            bdays = days
        } else if (unit == "Months") {
            days = 30 * value;
        } else if (unit == "Weeks") {
            days = 7* value;
        }

        if (unit == "Years" || unit == "Weeks" || unit == "Months" || unit == "Days") {
            let tempdate = dayjs().add(days, 'day');
            bdays = businessDiff(tempdate.toDate(), new Date());
        }

        if (bdays > currentSelectedCustomStatus?.ExtendByDaysMax) {
            setIsEGValid(false);
        } else {
            setIsEGValid(true);
        }
    }

    return (
        <Modal show={show} onHide={onHide} className="task-custom-panel" centered>
            <Modal.Header closeButton>
                <Modal.Title className="custom-title">
                    <h3 className="custom-subheading">{task.Name}</h3></Modal.Title>
            </Modal.Header>
            <div className="select-member-divider-line"></div>
            <Modal.Body>
                <div>
                    <LoadingOverlay area="tasks-reassign-panel" />
                    <Formik
                        validationSchema={yup.object({
                            Due: yup.date().typeError("Enter A Valid Date").required("End Date Required"),
                        })}
                        onSubmit={(values) => CustomStatusSubmit(values)}
                        initialValues={task}
                    >
                        {({
                            handleSubmit,
                            handleChange,
                            values,
                            touched,
                            errors,
                            setFieldValue,
                            isSubmitting
                        }) => (
                            <Form onSubmit={handleSubmit}>
                                <Form.Label>Task Status</Form.Label>
                                <Form.Control
                                    name="statusTypes"
                                    className='statusDropdown'
                                    value={noteAction}
                                    onChange={(e) => {
                                        setNoteAction(e.target.value);
                                    }}
                                    as="select"
                                >
                                    {task?.Workflow?.StatusListItems?.map((status) => <option key={status.Id} value={status.Id}>{status.Name}</option>)}
                                </Form.Control>
                                {/* Additional Outreach Use case */}
                                {hasDueDate && <Form.Group className="due-date-calendar">
                                    <Form.Label>Due Date</Form.Label>
                                    <FluentDatePicker
                                        id="Due"
                                        name="Due"
                                        placeholder="Click to select a end date"
                                        value={newDueDate}
                                        onChange={(value) => { setNewDueDate(value) }}
                                        minDate={new Date()}
                                        isInvalid={touched.Due && errors.Due !== undefined}
                                        feedback={errors.Due}
                                    />
                                </Form.Group>}
                                {/* Extension Granted Use Case */}
                                {hasExtendReminder &&
                                  <Form.Group className="row">
                                      <Form.Label className="col-md-12 custom-extend">Extended by <i className="red">*</i></Form.Label>
                                      <div className="col-4">
                                        <Form.Control
                                                name="taskName"
                                                id="taskName"
                                                type="number"
                                                className="form-control form-custom-control shadow-none extension-input-height"
                                                onKeyDown={(e) => {
                                                  (e.key === "Enter" ||
                                                    e.key === "." ||
                                                    e.key === "-") &&
                                                    e.preventDefault();
                                                }}
                                                min={1}
                                                value={selectedEGValue}
                                                onChange={(e) => {
                                                  e.target.value = e.target.value == 0 ? 1 : e.target.value;
                                                  setSelectedEGValue(e.target.value);
                                                  validateEG(e.target.value,selectedEGUnit);
                                                }}
                                                isInvalid={!isEGValid}
                                              />
                                      </div>
                                      <div  className="col-8 ">
                                      <DropdownMain
                                        values={extensionDuration.map(type => {
                                          return { id: type.id, name: type.name };
                                        })}
                                        onSelect={selectedUnit => {
                                          setSelectedEGUnit(selectedUnit.id);
                                          validateEG(selectedEGValue,selectedUnit.name);
                                        }}
                                        currentSelected={{id:selectedEGUnit, name:selectedEGUnit}}
                                      />
                                      </div>
                                      {!isEGValid &&
                                          <div className="col-12 extension-granted-error-text">The extension period cannot be beyond {currentSelectedCustomStatus?.ExtendByDaysMax} business days.</div>
                                      }
                                    </Form.Group>
                                }
                                <Form.Group className='custom-comment'>
                                    <Form.Label>Comments (optional)</Form.Label>
                                    <Form.Control
                                        value={comment}
                                        onChange={(e) => {
                                            setComment(e.target.value)
                                        }}
                                        as="textarea" rows="3" placeholder="Enter Comment" />
                                </Form.Group>
                                <Form.Group>
                                    <div onDrop={drop} onDragOver={dragOver} onDragEnter={dragEnter} onDragLeave={dragLeave}>
                                        <div className={"upload-file-box " + (showAlert ? " invalid-border" : "")}>
                                            <Form.Control
                                                className="browse-btn-hidden"
                                                type="file"
                                                multiple
                                                onChange={browseFile}
                                            />
                                            <Button className="browse-file-btn add-attachment-btn"></Button>
                                            {   selectedFiles?.map((file) =>
                                                <div className="single-file" key={file.name}>
                                                    <span className="file-name">{shortenFileName(file.name)}</span>
                                                    <Button className="delete-file-btn" onClick={(e) => { deleteBrowseFile(e, file.name) }}>x</Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {
                                        showAlert && alertVariant == "warning" &&
                                        displayAlert()
                                    }
                                </Form.Group>
                                <div className="action-buttons">
                                    <Button className="btn-wide btn-outlined" onClick={onHide} variant="primary">Cancel</Button>
                                    {
                                        currentSelectedCustomStatus?.ExtendReminderName !== null
                                        ?
                                        <Button
                                            type="submit"
                                            className="btn-wide"
                                            variant="primary"
                                            disabled={!isEGValid || isSubmitting}>Submit
                                        </Button>
                                        :
                                        <Button
                                            type="submit"
                                            className="btn-wide"
                                            variant="primary"
                                            disabled={isSubmitting}>Submit
                                        </Button>
                                    }
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </Modal.Body>
        </Modal>
    )
}

TaskCustomStatusPanel.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    type: PropTypes.string,
    task: PropTypes.object,
    refreshTasks: PropTypes.func,
    customStatusID: PropTypes.string,
    customStatusAction: PropTypes.string,
    customActionID: PropTypes.string,
    customActionName: PropTypes.string
};

export default TaskCustomStatusPanel;
